<template>
    <div>
        <h2
            class="text-lg font-medium leading-7 tracking-[-0.02em] text-iron-700"
        >
            {{ $t("event_types.event_detail.appearance") }}
        </h2>

        <p class="text-sm tracking-[-0.01em] text-iron-500">
            {{ $t("configurator.appearance_description") }}
        </p>

        <AvatarUpload
            class="mt-8"
            :image-url="eventTypeStore.imageUrl"
            :upload-url="route('app.images.store')"
            @file-uploaded-json="(url) => (eventTypeStore.imageUrl = url)"
        ></AvatarUpload>

        <div class="grid grid-cols-2 items-end gap-2">
            <div class="mt-4">
                <Label class="mb-1" for="pastMonths">{{
                    $t("appearances.pick_from_existing")
                }}</Label>
                <AppearancePicker
                    :selected-appearance="dirtyAppearance"
                    :appearances="appearances"
                    @on-selected="onApperanceSelected"
                />
            </div>

            <div>
                <CreateAppreanceModal @on-success="onAppearanceAdded">
                    <Button class="mt-4 w-[214px] gap-2" variant="outline">
                        <Plus class="text-iron-600" :size="20"></Plus>
                        {{ $t("appearances.create.title") }}
                    </Button>
                </CreateAppreanceModal>
            </div>
        </div>

        <div class="mt-4">
            <ColorPicker
                v-model:primary="dirtyAppearance.data.primaryColor"
                v-model:gray="dirtyAppearance.data.grayColor"
            />

            <ThemeSwitch v-model="dirtyAppearance.data.darkMode" />
            <BorderRadius v-model="dirtyAppearance.data.borderRadius" />
        </div>

        <div v-if="!hasSubscription" class="mt-4">
            <button @click="showProModal">
                <div class="flex items-center gap-2">
                    <Switch id="branding" :checked="true" />
                    <Label for="branding">
                        {{ $t("appearances.display_branding") }}</Label
                    >

                    <PaidFeatureIcon />
                </div>
            </button>
        </div>
    </div>
</template>

<script setup lang="ts">
import AvatarUpload from "@/components/ui/avatar-upload/AvatarUpload.vue";
import PaidFeatureIcon from "../../Atomic/PaidFeatureIcon.vue";
import AppearancePicker from "../../Appearances/components/AppearancePicker.vue";
import { IAppearance } from "@/types/IAppearance";
import { useEventTypeStore } from "../stores/eventTypeStore";
import route from "ziggy-js";
import { router } from "@inertiajs/vue3";
import { useHasSubscription } from "@/composables/useHasSubscription";
import ColorPicker from "../../Appearances/components/ColorPicker/index.vue";
import ThemeSwitch from "../../Appearances/components/ThemeSwitch.vue";
import BorderRadius from "../../Appearances/components/BorderRadius/BorderRadius.vue";
import Label from "@/components/ui/label/Label.vue";
import { ref, watch } from "vue";
import Switch from "@/components/ui/switch/Switch.vue";
import { watchDeep } from "@vueuse/core";
import Button from "@/components/ui/button/Button.vue";
import { Plus } from "lucide-vue-next";
import CreateAppreanceModal from "../../Appearances/CreateAppreanceModal.vue";
import useProDialog from "@/ProModal/composables/useProDialog";

const eventTypeStore = useEventTypeStore();
const { hasSubscription } = useHasSubscription();

const props = defineProps<{
    selectedAppearance: IAppearance;
    eventType: { id: number };
    appearances: IAppearance[];
}>();

const dirtyAppearance = ref<IAppearance>(props.selectedAppearance);

const changingAppearances = ref(false);

const proDialog = useProDialog();

watchDeep(dirtyAppearance, (newVal) => {
    if (!changingAppearances.value) {
        saveChanges();
    }
});

watch(
    () => props.selectedAppearance,
    (newVal) => {
        dirtyAppearance.value = newVal;
    },
);

function onAppearanceAdded() {
    changingAppearances.value = true;

    router.reload({ only: ["appearances"] });

    dirtyAppearance.value = props.appearances[props.appearances.length - 1];

    onApperanceSelected(dirtyAppearance.value);
}

function showProModal() {
    proDialog.show({
        feature: "remove_branding",
    });
}

function onApperanceSelected(appearance: IAppearance) {
    changingAppearances.value = true;
    router.post(
        route("app.event-types.appearances.assign", {
            eventType: props.eventType.id,
        }),
        {
            appearanceId: appearance.id,
        },
        {
            preserveState: true,
            onSuccess: () => {
                router.reload();

                setTimeout(() => {
                    changingAppearances.value = false;
                }, 500);
            },
        },
    );
}

function saveChanges() {
    changingAppearances.value = true;

    router.put(
        route("app.appearances.update", {
            appearance: props.selectedAppearance.id,
        }),
        {
            name: props.selectedAppearance.name,
            data: {
                primaryColor: dirtyAppearance.value.data.primaryColor,
                grayColor: dirtyAppearance.value.data.grayColor,
                borderRadius: dirtyAppearance.value.data.borderRadius,
                defaultSize: "mobile",
                darkMode: dirtyAppearance.value.data.darkMode,
            },
        },
        {
            preserveState: true,
            onSuccess: () => {
                router.reload();

                setTimeout(() => {
                    changingAppearances.value = false;
                }, 500);
            },
        },
    );
}
</script>
